/* eslint-disable jsx-a11y/label-has-for */
import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import useComponent from '../../../../hooks/useComponent';
import { setValue, setValueLocalized } from "../../../../store/actions";
import UIImage from "./ui/UIImage";
import Countdown from "./Countdown";
import Paragraph from "./ParagraphModule";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import items, { allItems } from "../ItemsModules/items";
import { copy, reorder } from "../../../../utils/dragAndDrop";
import uuid from "react-uuid";
import NativeVideoPlayerModule from "./NativeVideoPlayerModule";

const Components = ({
  activeLanguage,
  handleUpdateComponents,
  componentData,
  languages
}) => {
  const [showItem, setShowItem] = useState([]);
  const [selected, setSelected] = useState([]);
  const [itemsAll, setItemsAll] = useState([]);

  useEffect(() => {
    if (selected.length !== 0) return

    const defaultSelectedComponentes = [];

    componentData.map(async (comp) => {
      const readableNameMappedFromItems = items.filter(i => i.content === comp.type)[0];
      const readableName = readableNameMappedFromItems ? readableNameMappedFromItems.readableName : comp.type;

      const key = uuid();

      // create the objects to set the state of the drag and drop
      // we need to pass an id to make every module unique
      let selectedObj = {};
      if (Object.prototype.hasOwnProperty.call(comp, 'key')) {
        selectedObj = {
          content: comp.key,
          id: key,
          type: 'reusableItem',
          readableName: `Reusable item ${comp.key}`,
        };
      } else {
        if (comp.type === 'Carousel') {
          selectedObj = transformCarouselToDnD(comp);
          if (Object.prototype.hasOwnProperty.call(comp, 'areSlidesShort')) {
            selectedObj.component.areSlidesShort = comp.areSlidesShort;
          }
        } else if (comp.type === 'FilterContainer') {
          selectedObj = transformFilterToDnD(comp);
        } else if (comp.type === 'TagsRow') {
          selectedObj = transformTagsRowtoDnD(comp);
        } else {
          selectedObj = {
            content: comp.type,
            id: key,
            component: comp,
            readableName,
            placeholder: comp.placeholder ? comp.placeholder : false,
          };
        }
      }

      defaultSelectedComponentes.push(selectedObj);
    });

    setSelected(defaultSelectedComponentes);
  }, [componentData]);

  const getItemStyleType = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    border: '1px solid #dae1e7',
    borderRadius: '10px',
    background: 'white',
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const getItemStyleContent = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    color: 'white',
    padding: 8 * 1.5,
    paddingLeft: '40px',
    margin: `0 0 ${8 * 1.5}px 0`,
    borderRadius: '4px',
    fontFamily: 'GT-America-Mono-Regular',
    textTransform: 'uppercase',
    cursor: 'grab',
    zIndex: 9999,
    // change background colour if dragging
    background: isDragging ? '#1c3d5a' : '#9E9E9E',
    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const getListStyleType = (isDraggingOver) => ({
    background: isDraggingOver ? 'rgba(0,0,0,.5)' : 'white',
    overflowY: 'auto',
    borderRadius: '0 0 5px 5px',
    minHeight: `53px`,
  });

  const setVisibleItemFromDragBox = (id) => {
    showItem[id] = !showItem[id];
    setShowItem({ ...showItem });
  };

  const handlerSetComponents = (comp, index) => {
    const { component } = comp;
    const newSelected = selected;
    newSelected[index].component = { ...component };

    setSelected(newSelected);
    const components = getComponentsArray(newSelected)
    handleUpdateComponents(components)
  };

  const handleBlockClose = (id) => {
    const itemToBeRemovedIndex = selected.map(i => i.id).indexOf(id.toString());
    const arr = [...selected];
    arr.splice(itemToBeRemovedIndex, 1);
    setSelected(arr);


    const subcomponentsToBeRemovedIndex = selected.map(i => i.id).indexOf(id.toString());
    const arrSubcomponents = [...componentData];
    arrSubcomponents.splice(subcomponentsToBeRemovedIndex, 1);

    handleUpdateComponents(arrSubcomponents)
  };

  const handleModulesSearch = (e) => {
    e.preventDefault();
    const searchParam = e.target.value.toLowerCase();
    const searchParamLowerCased = searchParam ? searchParam.toLowerCase() : '';

    if (searchParamLowerCased && searchParamLowerCased.length > 2) {
      const filteredItems = allItems.filter(item => item.content.toLowerCase().includes(searchParamLowerCased) || item.readableName.toLowerCase().includes(searchParamLowerCased)) || [];
      setItemsAll(filteredItems);
    } else {
      setItemsAll([]);
    }
  };

  const getComponentsArray = (selectedComponents) => {
    const allMountableComponents = allItems.map(i => i.content);
    const componentsArray = selectedComponents.map((component) => {
      let modifiedCompWithCompId = { ...component };

      if (!component.component && !allMountableComponents.includes(component.content)) {
        const comp = { key: component.content, compId: `${component.type}_${uuid()}` };
        return comp;
      }
      if (!component.component) {
        modifiedCompWithCompId = {
          component: {
            type: component.content,
            compId: `${component.content}_${uuid()}`,
            placeholder: true,
          },
        };
      } else if (!component.component.compId) {
        modifiedCompWithCompId.component.compId = `${component.component.type}_${uuid()}`;
      }

      if (component.content === 'Carousel') return transformCarouselFromDnD(modifiedCompWithCompId);
      if (component.content === 'FilterContainer') return transformFilterFromDnD(modifiedCompWithCompId);
      return modifiedCompWithCompId.component;
    });

    return componentsArray || [];
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    switch (source.droppableId) {
      case destination.droppableId: {
        const newOrder = reorder(selected, source.index, destination.index);

        setSelected(newOrder);
        const components = getComponentsArray(newOrder)
        handleUpdateComponents(components)
        break;
      }

      // moving items from left to right columns (adding components to selected list)
      case 'all': {
        const makeCopy = copy(itemsAll, selected, source, destination);

        setSelected(makeCopy);
        const components = getComponentsArray(makeCopy)
        handleUpdateComponents(components)
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="all" isDropDisabled>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} className="w-full">
            <div className="border border-b-0 font-americaMonoRegular uppercase text-sm py-1 flex items-center pl-1 flex flex-row justify-between">
              <div className="flex flex-row items-center font-semibold cursor-pointer">
                <span className="font-icon icon-search text-lg pr-4" />
                <input
                  className="p-2 outline-none uppercase"
                  type="text"
                  onChange={handleModulesSearch}
                  placeholder="TYPE TO SEARCH MODULES"
                />
              </div>
              <span className="cursor-pointer font-icon text-xxs" />
            </div>
            <div>
              {itemsAll && itemsAll.length
                ? itemsAll.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyleContent(snapshot.isDragging, provided.draggableProps.style)}>
                        {item.readableName}
                      </div>
                    )}
                  </Draggable>
                )) : null}
              {provided.placeholder}
            </div>
          </div>
        )}
      </Droppable>
      <Droppable droppableId="selected">
        {(provided, snapshot) => (
          <div className="border relative w-full" ref={provided.innerRef} style={getListStyleType(snapshot.isDraggingOver)}>
            {selected.length > 0
              && selected.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                  activeLanguage={activeLanguage}
                >
                  {(provided, snapshot, component) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={getItemStyleType(snapshot.isDragging, provided.draggableProps.style)}
                      className="relative mx-5 my-2"
                    >
                      <div className="flex">
                        <div className="w-10">
                          <div className="h-full w-full bg-greyFilterItems text-greyFilter text-30 flex items-center justify-center rounded-tl-lg roundend-bl-lg"
                            {...provided.dragHandleProps}
                          >
                            =
                          </div>
                        </div>
                        <div className="w-full">
                          <div className="w-full p-4">
                            <div className="flex justify-between" {...provided.dragHandleProps}>
                              {item.placeholder ? (
                                <div className="bg-blue-darker block leading-normal font-bold opacity-25 p-2 pt-3 rounded text-white text-xs tracking-wide uppercase">
                                  {item.readableName}
                                </div>
                              ) : (
                                <button
                                  className="bg-blue-darker block font-bold p-2 rounded text-white text-xs tracking-wide uppercase relative pr-8"
                                  type="button"
                                  onClick={() => setVisibleItemFromDragBox(item.id)}
                                >
                                  {item.readableName}

                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 h-10 top-0">
                                    <svg
                                      className="fill-current h-4 w-4"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                    </svg>
                                  </div>
                                </button>
                              )}
                              <button
                                className="bg-blue-darker font-bold p-2 rounded tracking-wide"
                                type="button"
                                onClick={() => {
                                  handleBlockClose(item.id);
                                }}
                              >
                                <span className="font-icon icon-close-btn1 text-base text-white" />
                              </button>
                            </div>
                          </div>
                          {(showItem[item.id] && !item.placeholder) && (
                            <div className="pb-4 px-4 w-full">
                              {item.content === 'Countdown' &&
                                <Countdown
                                  handlerSetComponents={handlerSetComponents}
                                  id={item.id}
                                  components={selected}
                                  activeLanguage={activeLanguage}
                                />}
                              {item.content === 'NormalBodyText' &&
                                <Paragraph
                                  languages={languages}
                                  handlerSetComponents={handlerSetComponents}
                                  id={item.id}
                                  components={selected}
                                  activeLanguage={activeLanguage}
                                />}
                              {item.content === 'NativeVideoPlayer' &&
                                <NativeVideoPlayerModule
                                  languages={languages}
                                  handlerSetComponents={handlerSetComponents}
                                  id={item.id}
                                  components={selected}
                                  activeLanguage={activeLanguage}
                                />}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

const Input = ({
  label = '',
  handleInputValue,
  value,
  name,
  type
}) => (
  <div className="w-full">
    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor={name}>{label}</label>
    <input
      className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
      type={type}
      onChange={handleInputValue}
      name={name}
      value={value}
    />
  </div>
)

const initialState = {
  data: {
    type: 'ImageWrapper',
    image: {
      en: '',
      es: '',
      ca: '',
      pt: '',
    },
    desktop: {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
    mobile: {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
    components: []
  },
  error: false,
};

const ImageWrapper = ({
  handlerSetComponents,
  components,
  id,
  reusableComponent,
  activeLanguage,
  languages
}) => {
  const { state: { data }, dispatch } = useComponent({ initialState, setComponents: handlerSetComponents, components, id, reusableComponent });
  const [device, setDevice] = useState("desktop")

  const handleUpdateImage = language => (imageUrl) => setValueLocalized(dispatch)('image', imageUrl, language);
  const handleUpdateComponents = (componentsToUpdate) => setValue(dispatch)('components', componentsToUpdate);
  const handleInputValue = (e) => {
    const { value, name } = e.target;
    const parseValue = parseInt(value, 10);
    setValueLocalized(dispatch)(device, parseValue, name);
  };

  return (
    <div className="flex flex flex-wrap w-full">
      <div className="flex flex flex-wrap w-full">
        <div className="flex w-full md:w-full md:mb-0 mt-2">
          <div className="w-full pr-4">
            <UIImage label="Image" activeLanguage={activeLanguage} getImageUrl={handleUpdateImage(activeLanguage)} imageUrl={data.image} />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap w-full mb-4">
        <div className="flex">
          <span
            className={`tab pl-5 flex items-center cursor-pointer font-semibold text-xs rounded-t active-white border-b-0 ${device === 'desktop' ? 'border' : ''}`}
            data-name="desktop"
            onClick={() => setDevice('desktop')}
          >
            Desktop
          </span>
          <span
            className={`tab pl-5 flex items-center cursor-pointer font-semibold text-xs rounded-t active-white border-b-0 ${device === 'mobile' ? 'border' : ''}`}
            data-name="mobile"
            onClick={() => setDevice('mobile')}
          >
            Mobile
          </span>
        </div>
        <div className="flex flex-wrap w-full md:w-full md:mb-0 p-8 border">
          <div className="flex w-full">
            <div className="w-1/2 pr-4">
              <Input
                label="Padding top"
                handleInputValue={handleInputValue}
                value={data[device]?.paddingTop}
                name="paddingTop"
                type="number"
              />
            </div>
            <div className="w-1/2 pr-4">
              <Input
                label="Padding bottom"
                handleInputValue={handleInputValue}
                value={data[device]?.paddingBottom}
                name="paddingBottom"
                type="number"
              />
            </div>
          </div>

          <div className="flex w-full">
            <div className="w-1/2 pr-4">
              <Input
                label="Padding left"
                handleInputValue={handleInputValue}
                value={data[device]?.paddingLeft}
                name="paddingLeft"
                type="number"
              />
            </div>
            <div className="w-1/2 pr-4">
              <Input
                label="Padding right"
                handleInputValue={handleInputValue}
                value={data[device]?.paddingRight}
                name="paddingRight"
                type="number"
              />
            </div>
          </div>
        </div>
      </div>

      <Components
        activeLanguage={activeLanguage}
        handleUpdateComponents={handleUpdateComponents}
        componentData={data.components}
        languages={languages}
      />
    </div >
  );
};

ImageWrapper.defaultProps = {
  title: PropTypes.string,
  name: PropTypes.string,
  dispatch: PropTypes.func,
  defaultValue: PropTypes.shape({}),
};

export default ImageWrapper;
